import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mark } from './ui/kit';
import { MARKS } from '../lib/marks';

type VerificationStatus = 'loading' | 'success' | 'error' | 'already_verified' | 'expired';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage('No verification token provided');
        return;
      }

      try {
        await axios.get(`/api/auth/verify-email/${token}`);
        setStatus('success');

        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const errorCode = error.response?.data?.error;

          switch (errorCode) {
            case 'ALREADY_VERIFIED':
              setStatus('already_verified');
              break;
            case 'TOKEN_EXPIRED':
              setStatus('expired');
              setErrorMessage('Your verification link has expired. Please request a new one.');
              break;
            case 'INVALID_TOKEN':
              setStatus('error');
              setErrorMessage('This verification link is invalid or has already been used.');
              break;
            default:
              setStatus('error');
              setErrorMessage(error.response?.data?.message || 'Failed to verify email');
          }
        } else {
          setStatus('error');
          setErrorMessage('An unexpected error occurred');
        }
      }
    };

    verifyEmail();
  }, [token, navigate]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <div className="w-16 h-16 mx-auto mb-6">
              <svg
                className="w-full h-full animate-spin text-accent"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-primary mb-2">Verifying your email...</h1>
            <p className="text-muted">Please wait while we verify your email address.</p>
          </>
        );

      case 'success':
        return (
          <>
            <Mark src={MARKS.verified} size="lg" className="mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-primary mb-2">Email Verified!</h1>
            <p className="text-muted mb-6">
              Your email has been successfully verified. You now have full access to all features.
            </p>
            <p className="text-sm text-muted">Redirecting to dashboard...</p>
          </>
        );

      case 'already_verified':
        return (
          <>
            <Mark src={MARKS.verified} size="lg" className="mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-primary mb-2">Already Verified</h1>
            <p className="text-muted mb-6">
              Your email address has already been verified. You're all set!
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center px-6 py-2.5 bg-primary hover:bg-ink-soft text-white font-medium rounded-full transition-colors"
            >
              Go to Dashboard
            </Link>
          </>
        );

      case 'expired':
        return (
          <>
            {/* A spent fuse — the link did its one job and is done. */}
            <Mark src={MARKS.broken} size="lg" className="mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-primary mb-2">Link Expired</h1>
            <p className="text-muted mb-6">{errorMessage}</p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-6 py-2.5 bg-primary hover:bg-ink-soft text-white font-medium rounded-full transition-colors"
            >
              Log in to resend
            </Link>
          </>
        );

      case 'error':
      default:
        return (
          <>
            <Mark src={MARKS.broken} size="lg" className="mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-primary mb-2">Verification Failed</h1>
            <p className="text-muted mb-6">{errorMessage}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/login"
                className="inline-flex items-center justify-center px-6 py-2.5 bg-primary hover:bg-ink-soft text-white font-medium rounded-full transition-colors"
              >
                Back to Login
              </Link>
            </div>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-xl shadow-sm border border-zinc-200 p-8 text-center"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <img
            src="/foundlio-on-light.svg"
            alt="Foundlio"
            className="h-8 w-auto"
          />
          <img
            src="/foundlio-on-dark.svg"
            alt="Foundlio"
            className="h-8 w-auto hidden"
          />
          <span className="font-display font-bold text-primary text-xl tracking-tight">
            Foundlio
          </span>
        </div>

        {renderContent()}
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
